import "./General.scss";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IconSvg } from "../../../../../../../utils/constants/icons/iconSvg.constant";
import { DashedProgressBar } from "../../../../../../../components/common/DashedProgressBar/DashedProgressBar";
import { CustomSelect } from "../../../../../../../components/ui/CustomSelect/CustomSelect";
import { Button } from "../../../../../../../components/ui/Buttons/Button/Button";
import { Toggle } from "../../../../../../../components/ui/Toggle/Toggle";
import { classNames } from "../../../../../../../utils/helpers/classNames";
import { useResize, useTranslation } from "../../../../../../../hooks";
import { Tooltip } from "../../../../../../../components/ui/Tooltip/Tooltip";
import { ProxyList } from "./components/components/ProxyList/ProxyList";
import { Instruction } from "./components/components/Instruction/Instruction";

export const General = () => {
  const { width } = useResize();
  const [isInstructionOpen, setIsInstructionOpen] = useState(false);

  const navigate = useNavigate();

  const {
    cabinet: {
      orders: {
        residential: {
          activeTo, tariff, changeTariff,
          used24h, totalUsed, reserveUsed,
          remaining, addGb, options,
          rotationMethod, apply, autoRenewal,
          instruction, apiAccess, pausedTitle,
          pausedSub, pausedDescription
        }
      }
    }
  } = useTranslation();

  const { skin } = useSelector((state) => state.header);

  const wrappersRef = useRef({
    service: null,
    options: null
  });

  // eslint-disable-next-line no-unused-vars
  const [isAvailable, setIsAvailable] = useState(true);
  const [isServiceOpen, setIsServiceOpen] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  useEffect(() => {
    const services = wrappersRef?.current?.service;
    const options = wrappersRef?.current?.options;

    if (!services || !options) return;

    services.style.height = "auto";
    options.style.height = "auto";

    setIsServiceOpen(true);
    setIsOptionsOpen(true);
  }, [width]);

  const blocksHandler = (key) => {
    if (window?.innerWidth && window.innerWidth >= 993) return;

    const element = wrappersRef?.current?.[key];

    if (!element) return;

    const fullHeight = element.scrollHeight;

    const isOpen = key === "service" ? isServiceOpen : isOptionsOpen;
    const setter = key === "service" ? setIsServiceOpen : setIsOptionsOpen;

    if (isOpen) {
      element.style.height = "0px";
      setter(false);

      return;
    }

    element.style.height = `${fullHeight}px`;
    setter(true);
  };

  return (
    <div className="general">
      {isAvailable ? (
        <div className="general__available-wrapper">
          <div className="general__main-container">
            <div className={classNames("general__container general__service-container",
              { "general__service-container--open": isServiceOpen }
            )}>
              <div className="general__title" onClick={() => blocksHandler("service")}>
                <div>Service information</div>
                <IconSvg tag="indicator" />
              </div>
              <div
                ref={(el) => (wrappersRef.current.service = el)}
                className="general__service-wrapper"
              >
                <ul className="general__service">
                  <li className="general__service-item">
                    <div className="general__label">{tariff}:</div>
                    <div className="general__service-value">100 GB <span>{`(${activeTo} 19/11/2024)`}</span></div>
                    <div className="general__service-action">
                      <IconSvg tag="pen"/>
                      <div>{changeTariff}</div>
                    </div>
                  </li>
                  <li className="general__service-item">
                    <div className="general__label general__used-24h">
                      {used24h}:
                      <Tooltip label="Расход траффика за предыдущие 24 часа от 00:00 до 00:00">
                        <IconSvg tag="newInfo"/>
                      </Tooltip>
                    </div>
                    <div className="general__usage">
                      <div className="general__usage-total">
                        <div className="general__service-value">{totalUsed}</div>
                        <div className="general__usage-total-bar">
                          <DashedProgressBar color="#27C660" bgColor="#383838" current={55} total={100}/>
                        </div>
                        <div className="general__service-value">24GB</div>
                      </div>
                      <div className="general__usage-reserve">
                        <div className="general__service-value">{reserveUsed}</div>
                        <div className="general__usage-total-bar">
                          <DashedProgressBar color="#FFC123" bgColor="#383838" current={70} total={100}/>
                        </div>
                        <div className="general__service-value">5 GB</div>
                      </div>
                    </div>
                    <div className="media__max-width-992 general__total-used">
                      <div className="general__progress-bar">
                        <DashedProgressBar color="#27C660" bgColor="#066D2E" current={76} total={100}/>
                      </div>
                      <div className="general__progress-bar-details">
                        <div className="general__label general__progress-bar-label">{remaining}:</div>
                        <div className="general__progress-bar-value-container">
                          <div className="general__progress-bar-remaining general__progress-bar-remaining--green">76
                            GB
                          </div>
                          <span>/</span>
                          <div className="general__progress-bar-value">100 GB</div>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="general__service-item">
                    <div className="general__label">{remaining}:</div>
                    <div className="general__rest-container">
                      <div className="general__usage-total">
                        <div className="general__service-value">{totalUsed}</div>
                        <div className="general__usage-total-bar">
                          <DashedProgressBar color="#066D2E" bgColor="#383838" current={55} total={100}/>
                        </div>
                        <div className="general__service-value">24GB</div>
                      </div>
                      <div className="general__usage-reserve">
                        <div className="general__service-value">{reserveUsed}</div>
                        <div className="general__usage-total-bar">
                          <DashedProgressBar color="#926004" bgColor="#383838" current={70} total={100}/>
                        </div>
                        <div className="general__service-value">5 GB</div>
                      </div>
                    </div>
                    <div className="general__service-action">
                      <IconSvg tag="thinPlus"/>
                      <div>{addGb}</div>
                    </div>
                  </li>
                </ul>
                <div className="general__bars">
                  <div className="media__min-width-993 general__total-used">
                    <div className="general__progress-bar">
                      <DashedProgressBar color="#27C660" bgColor="#066D2E" current={76} total={100}/>
                    </div>
                    <div className="general__progress-bar-details">
                      <div className="general__label general__progress-bar-label">{remaining}:</div>
                      <div className="general__progress-bar-value-container">
                        <div className="general__progress-bar-remaining general__progress-bar-remaining--green">76 GB
                        </div>
                        <span>/</span>
                        <div className="general__progress-bar-value">100 GB</div>
                      </div>
                    </div>
                  </div>
                  <div className="general__reserve-used">
                    <div className="general__progress-bar">
                      <DashedProgressBar color="#FFC123" bgColor="#926001" current={35} total={100}/>
                    </div>
                    <div className="general__progress-bar-details">
                      <div className="general__label general__progress-bar-label">{remaining}:</div>
                      <div className="general__progress-bar-value-container">
                        <div className="general__progress-bar-remaining general__progress-bar-remaining--orange">76 GB
                        </div>
                        <span>/</span>
                        <div className="general__progress-bar-value">100 GB</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={classNames("general__container general__options-container",
              { "general__options-container--open": isOptionsOpen }
            )}>
              <div className="general__title" onClick={() => blocksHandler("options")}>
                <div>{options}</div>
                <IconSvg tag="indicator"/>
              </div>
              <ul ref={(el) => (wrappersRef.current.options = el)} className="general__options">
                <li className="general__option">
                  <div className="general__rotation-label-wrapper">
                    <div className="general__label">{rotationMethod}</div>
                    <Tooltip label="Some info">
                      <IconSvg tag="newInfo"/>
                    </Tooltip>
                  </div>
                  <div className="general__rotation-container">
                    <CustomSelect/>
                    <Button tab="confirm">{apply}</Button>
                  </div>
                </li>
                <li className="general__option">
                  <div className="general__label">{autoRenewal}:</div>
                  <div className="general__renewal-container">
                    <Toggle/>
                    <div className="general__renewal-label">1 month</div>
                  </div>
                </li>
                <li className="general__option">
                  <div className="general__instruction" onClick={() => setIsInstructionOpen(true)}>
                    <div>{instruction}</div>
                    <IconSvg tag="arrow24"/>
                  </div>
                  <div className="general__api-link" onClick={() => navigate("?tab=documentation")}>
                    <div>{apiAccess}</div>
                    <IconSvg tag="externalLink"/>
                  </div>
                </li>
              </ul>
            </div>
            <div className="general__container">
              <ProxyList/>
            </div>
            {isInstructionOpen && <Instruction onClose={() => setIsInstructionOpen(false)} />}
          </div>
        </div>
      ) : (
        <div className="general__paused">
          <div className="general__paused-container">
            <div className="general__paused-icon">
              <IconSvg tag={`residentalPaused_${skin}`}/>
            </div>
            <div className="general__paused-text">
              {pausedTitle} <br/> {pausedSub}
            </div>
            <div className="general__paused-description">
              {pausedDescription}
            </div>
            <div className="general__service-action">
              <IconSvg tag="thinPlus"/>
              <div>{addGb}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
